import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import { Checkbox } from "carbon-react/esm/components/checkbox";
import DateInput from "carbon-react/esm/components/date";
import {
  FlatTable,
  FlatTableBody,
  FlatTableCell,
  FlatTableHead,
  FlatTableHeader,
  FlatTableRow,
} from "carbon-react/esm/components/flat-table";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";
import Textbox from "carbon-react/esm/components/textbox";
import { ApplicationApiError } from "../api/applicationApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { DateValue, FinancialAmount } from "../components/FinancialValue";
import { MetricCard } from "../components/MetricCard";
import {
  Option,
  SearchableSelect as Select,
} from "../components/SearchableSelect";
import { StatusPill } from "../components/StatusPill";
import { usePaginatedRows } from "../components/TablePagination";
import {
  getPostingView,
  performPostingAction,
  type EligibleEvent,
  type PostingBatch,
  type PostingPreview,
} from "../api/postingApi";

export function PostingPage({
  onNavigate,
}: {
  onNavigate(path: string): void;
}) {
  const { api } = useContext(EmbeddedSessionContext);
  const [events, setEvents] = useState<EligibleEvent[] | null>(null);
  const [batches, setBatches] = useState<PostingBatch[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [filter, setFilter] = useState({ eventType: "", search: "" });
  const [postingDate, setPostingDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [preview, setPreview] = useState<PostingPreview | null>(null);
  const [overridePostingDate, setOverridePostingDate] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => {
    if (!api) return;
    void Promise.all([
      getPostingView<{ events: EligibleEvent[] }>(api, "eligible"),
      getPostingView<{ batches: PostingBatch[] }>(api, "batches"),
    ])
      .then(([eligible, history]) => {
        setEvents(eligible.events);
        setBatches(history.batches);
      })
      .catch(() => setError("Posting data could not be loaded."));
  };
  useEffect(load, [api]);
  const visible = (events ?? []).filter(
    (event) =>
      (!filter.eventType || event.event_type === filter.eventType) &&
      (!filter.search ||
        `${event.loan_name} ${event.loan_number} ${event.lender_name ?? ""}`
          .toLocaleLowerCase()
          .includes(filter.search.toLocaleLowerCase())),
  );
  const eligibleRows = usePaginatedRows(visible);
  const historyRows = usePaginatedRows(batches);
  if (!events) return <Loader loaderLabel="Loading posting" size="medium" />;
  const updateSelection = (eventId: string, checked: boolean) => {
    const next = checked
      ? [...selected, eventId]
      : selected.filter((id) => id !== eventId);
    setSelected(next);
    const paymentDate = paymentPostingDate(events, next);
    if (paymentDate) setPostingDate(paymentDate);
  };
  const createPreview = async () => {
    if (!api) return;
    setWorking(true);
    setError(null);
    try {
      const nextPreview = await performPostingAction<PostingPreview>(api, {
        action: "create_preview",
        eventIds: selected,
        postingDate,
      });
      setOverridePostingDate("");
      setPreview(nextPreview);
    } catch (actionError) {
      setError(
        postingErrorMessage(
          actionError,
          "The preview could not be created. Check journals, accounts, dimensions, and event dates.",
        ),
      );
    } finally {
      setWorking(false);
    }
  };
  const post = async () => {
    if (!api || !preview) return;
    setWorking(true);
    try {
      const result = await performPostingAction<{ batchId: string }>(
        api,
        postPreviewRequest(preview.preview.id, overridePostingDate),
      );
      setPreview(null);
      setOverridePostingDate("");
      setSelected([]);
      onNavigate(`/posting/${result.batchId}`);
    } catch (actionError) {
      setError(
        postingErrorMessage(
          actionError,
          "Sage posting failed. Open the batch result to retry failed events.",
        ),
      );
    } finally {
      setWorking(false);
    }
  };
  return (
    <div className="operational-stack">
      {error ? (
        <Message open title="Posting action failed" variant="error">
          {error}
        </Message>
      ) : null}
      <section aria-label="Posting summary" className="metric-grid">
        <MetricCard icon="document_tick" label="Eligible events" primary>
          {events.length}
        </MetricCard>
        <MetricCard icon="tick" label="Selected events">
          {selected.length}
        </MetricCard>
        <MetricCard icon="cash" label="Eligible amount">
          <FinancialAmount
            currency="USD"
            value={events
              .reduce((total, event) => total + Number(event.amount), 0)
              .toFixed(2)}
          />
        </MetricCard>
        <MetricCard icon="batch" label="Posting batches">
          {batches.length}
        </MetricCard>
      </section>
      <section className="operational-section">
        <div className="section-heading-row">
          <div>
            <h2>Eligible accounting events</h2>
            <p>
              Select activated schedule events to preview as balanced Sage
              journal entries.
            </p>
          </div>
          <Button
            disabled={selected.length === 0 || working}
            onClick={() => void createPreview()}
            variant="default"
            variantType="primary"
          >
            Preview {selected.length || ""} events
          </Button>
        </div>
        <div className="filter-bar">
          <Textbox
            label="Loan or lender"
            onChange={(event) =>
              setFilter({ ...filter, search: event.target.value })
            }
            value={filter.search}
          />
          <Select
            id="posting-event-type"
            label="Event type"
            onChange={(event) =>
              setFilter({ ...filter, eventType: event.target.value })
            }
            value={filter.eventType}
          >
            <Option text="All event types" value="" />
            {[
              "funding",
              "interest_accrual",
              "payment_application",
              "cash_settlement",
              "financing_fee_amortization",
              "additional_principal",
              "balloon_payment",
              "payoff",
            ].map((value) => (
              <Option
                key={value}
                text={value.replaceAll("_", " ")}
                value={value}
              />
            ))}
          </Select>
          <DateInput
            allowEmptyValue
            dateFormatOverride="M/d/yyyy"
            datePickerAriaLabel="Choose posting date"
            id="posting-date"
            inputHint="M/D/YYYY"
            inputWidth={240}
            label="Posting date"
            maxWidth="100%"
            onChange={(event) =>
              setPostingDate(
                normalizePostingDateInput(
                  event.target.value.rawValue ??
                    event.target.value.formattedValue,
                ),
              )
            }
            pickerProps={{
              captionLayout: "dropdown",
              endMonth: new Date(2200, 11),
              startMonth: new Date(1900, 0),
            }}
            value={formatPostingDateInput(postingDate)}
          />
        </div>
        <FlatTable
          caption="Eligible accounting events"
          colorTheme="light"
          overflowX="auto"
        >
          <FlatTableHead>
            <FlatTableRow>
              <FlatTableHeader>Select</FlatTableHeader>
              <FlatTableHeader>Event date</FlatTableHeader>
              <FlatTableHeader>Loan</FlatTableHeader>
              <FlatTableHeader>Lender / entity</FlatTableHeader>
              <FlatTableHeader>Event</FlatTableHeader>
              <FlatTableHeader>Amount</FlatTableHeader>
              <FlatTableHeader>Status</FlatTableHeader>
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {eligibleRows.visibleRows.map((event) => (
              <FlatTableRow key={event.id}>
                <FlatTableCell>
                  <Checkbox
                    checked={selected.includes(event.id)}
                    label=""
                    onChange={(change) =>
                      updateSelection(event.id, change.target.checked)
                    }
                  />
                </FlatTableCell>
                <FlatTableCell>
                  <DateValue value={event.event_date} />
                </FlatTableCell>
                <FlatTableCell>
                  {event.loan_name}
                  <br />
                  <span className="muted-text">{event.loan_number}</span>
                </FlatTableCell>
                <FlatTableCell>
                  {event.lender_name ?? "—"}
                  <br />
                  <span className="muted-text">{event.entity_name}</span>
                </FlatTableCell>
                <FlatTableCell>
                  {event.event_type.replaceAll("_", " ")}
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount currency="USD" value={event.amount} />
                </FlatTableCell>
                <FlatTableCell>
                  <StatusPill label={event.posting_status} />
                </FlatTableCell>
              </FlatTableRow>
            ))}
          </FlatTableBody>
        </FlatTable>
        {eligibleRows.pagination}
      </section>
      <section className="operational-section">
        <h2>Posting history</h2>
        <FlatTable
          caption="Posting batches"
          colorTheme="light"
          overflowX="auto"
        >
          <FlatTableHead>
            <FlatTableRow>
              <FlatTableHeader>Created</FlatTableHeader>
              <FlatTableHeader>Posting date</FlatTableHeader>
              <FlatTableHeader>Loans / events</FlatTableHeader>
              <FlatTableHeader>Total</FlatTableHeader>
              <FlatTableHeader>Sage state</FlatTableHeader>
              <FlatTableHeader>Status</FlatTableHeader>
            </FlatTableRow>
          </FlatTableHead>
          <FlatTableBody>
            {historyRows.visibleRows.map((batch) => (
              <FlatTableRow
                key={batch.id}
                onClick={() => onNavigate(`/posting/${batch.id}`)}
              >
                <FlatTableCell>
                  {new Date(batch.created_at).toLocaleString()}
                </FlatTableCell>
                <FlatTableCell>
                  <DateValue value={batch.posting_date} />
                </FlatTableCell>
                <FlatTableCell>
                  {batch.loan_count} / {batch.event_count}
                </FlatTableCell>
                <FlatTableCell>
                  <FinancialAmount currency="USD" value={batch.total_debits} />
                </FlatTableCell>
                <FlatTableCell>{batch.sage_state}</FlatTableCell>
                <FlatTableCell>
                  <StatusPill
                    label={batch.status}
                    tone={
                      batch.status === "posted"
                        ? "positive"
                        : batch.status === "failed"
                          ? "negative"
                          : "warning"
                    }
                  />
                </FlatTableCell>
              </FlatTableRow>
            ))}
          </FlatTableBody>
        </FlatTable>
        {historyRows.pagination}
      </section>
      <ConfirmationDialog
        confirmLabel="Post to Sage"
        contentClassName="posting-preview-dialog__content"
        dialogClassName="posting-preview-dialog"
        isConfirming={working}
        onCancel={() => {
          setPreview(null);
          setOverridePostingDate("");
        }}
        onConfirm={() => void post()}
        open={preview !== null}
        title="Confirm Sage journal posting"
      >
        {preview ? (
          <div className="confirmation-summary">
            <p>
              {preview.eventCount} events · {preview.lineCount} journal lines ·
              expected GL posting date{" "}
              <DateValue value={preview.preview.posting_date} />
            </p>
            <DateInput
              allowEmptyValue
              dateFormatOverride="M/d/yyyy"
              datePickerAriaLabel="Choose override GL posting date"
              id="override-gl-posting-date"
              inputHint="M/D/YYYY"
              inputWidth={240}
              label="Override GL posting date"
              maxWidth="100%"
              onChange={(event) =>
                setOverridePostingDate(
                  normalizePostingDateInput(
                    event.target.value.rawValue ??
                      event.target.value.formattedValue,
                  ),
                )
              }
              pickerProps={{
                captionLayout: "dropdown",
                endMonth: new Date(2200, 11),
                startMonth: new Date(1900, 0),
              }}
              value={formatPostingDateInput(overridePostingDate)}
            />
            <p>
              <strong>Debits:</strong>{" "}
              <FinancialAmount
                currency="USD"
                value={preview.preview.total_debits}
              />{" "}
              · <strong>Credits:</strong>{" "}
              <FinancialAmount
                currency="USD"
                value={preview.preview.total_credits}
              />
            </p>
            {preview.lines.map((line, index) => (
              <div key={`${line.eventId}-${index}`}>
                {line.side} · {line.description} ·{" "}
                <FinancialAmount currency="USD" value={line.amount} />
              </div>
            ))}
          </div>
        ) : null}
      </ConfirmationDialog>
    </div>
  );
}

export function paymentPostingDate(
  events: EligibleEvent[],
  selectedIds: string[],
): string | null {
  const selected = events.filter((event) => selectedIds.includes(event.id));
  const paymentTypes = new Set(["payment_application", "cash_settlement"]);
  const dates = [...new Set(selected.map((event) => event.event_date))];
  return selected.length > 0 &&
    selected.every((event) => paymentTypes.has(event.event_type)) &&
    dates.length === 1
    ? dates[0]!
    : null;
}

export function postPreviewRequest(
  previewId: string,
  overridePostingDate: string,
): Record<string, unknown> {
  return {
    action: "post_preview",
    overridePostingDate: overridePostingDate || null,
    previewId,
  };
}

export function normalizePostingDateInput(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return value.toISOString().slice(0, 10);
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  const isoMatch = /^(\d{4}-\d{2}-\d{2})/u.exec(trimmed);
  if (isoMatch) return isoMatch[1]!;
  const displayMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u.exec(trimmed);
  if (!displayMatch) return trimmed;
  return `${displayMatch[3]}-${displayMatch[1]!.padStart(2, "0")}-${displayMatch[2]!.padStart(2, "0")}`;
}

export function formatPostingDateInput(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  return match ? `${Number(match[2])}/${Number(match[3])}/${match[1]}` : value;
}
function postingErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApplicationApiError
    ? `${error.message} (${error.code}; correlation ${error.correlationId})`
    : fallback;
}
